import { useRef, useState } from 'react';
import { MAX_UPLOAD_BYTES } from '@affily/shared/constants';
import { api } from '../lib/api.js';
import { Button } from './ui.jsx';

const LIMIT_MB = MAX_UPLOAD_BYTES / 1024 / 1024;

/**
 * Pick files and upload each one. The server checks the type by content and
 * answers `{ url, mime, … }`; `onUploaded` gets that once per file, in the
 * order chosen. Nothing is saved to the profile until the form is.
 */
export function UploadButton({
  accept,
  multiple = false,
  onUploaded,
  onError,
  disabled,
  variant = 'outline',
  className = '',
  children,
}) {
  const input = useRef(null);
  const [busy, setBusy] = useState(false);

  async function onChange(e) {
    const files = [...e.target.files];
    // Cleared so picking the same file again (after removing it) still fires.
    e.target.value = '';
    if (!files.length) return;

    setBusy(true);
    onError?.('');
    try {
      for (const file of files) {
        // Checked here too so a 200MB phone video fails now, not after uploading.
        if (file.size > MAX_UPLOAD_BYTES) {
          const hint = file.type.startsWith('video/') ? ' Add longer videos as a YouTube link.' : '';
          throw new Error(`${file.name} is over the ${LIMIT_MB}MB limit.${hint}`);
        }
        const body = new FormData();
        body.append('file', file);
        onUploaded(await api.upload('/v1/uploads', body));
      }
    } catch (err) {
      onError?.(err.message);
    } finally {
      setBusy(false);
    }
  }

  return (
    <>
      <input
        ref={input}
        type="file"
        accept={accept}
        multiple={multiple}
        onChange={onChange}
        className="hidden"
      />
      <Button
        type="button"
        variant={variant}
        size="sm"
        className={className}
        disabled={disabled || busy}
        onClick={() => input.current?.click()}
      >
        {busy ? 'Uploading…' : children}
      </Button>
    </>
  );
}
